/**
 * GoodGuardian — Co-Parent Hub Screen
 * Invite by email, manage pending invites, accept by token/QR, view linked partner, disconnect
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { useApp } from '@/context/AppContext'
import * as api from '@/utils/auth'

// ── QR Code Modal ─────────────────────────────────────────────────────────────
function QRModal({ token, onClose }) {
  const acceptUrl = `${window.location.origin}/coparent?token=${token}`
  const qrSrc     = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=12&data=${encodeURIComponent(acceptUrl)}`
  const [copied, setCopied] = useState(false)

  function copyLink() {
    navigator.clipboard.writeText(acceptUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-6 lg:pb-0">
      <div className="bg-white rounded-3xl w-full max-w-sm lg:max-w-md p-6 shadow-2xl"
        role="dialog" aria-modal="true" aria-label="Co-parent invite QR code">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Scan to Connect</h3>
            <p className="text-sm text-slate-500 mt-0.5">Show this to your co-parent to scan</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
            aria-label="Close QR modal"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* QR Code */}
        <div className="flex items-center justify-center bg-white rounded-2xl border border-slate-100 p-3 mb-4">
          <img
            src={qrSrc}
            alt="QR code to accept co-parent invite"
            width={220}
            height={220}
            className="rounded-xl"
          />
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 rounded-xl px-4 py-3 mb-4 text-sm text-blue-700 space-y-1">
          <p className="font-semibold">How it works:</p>
          <p>1. Your co-parent opens their camera and scans this code</p>
          <p>2. They'll be taken to GoodGuardian to accept</p>
          <p>3. Once accepted, your accounts link automatically</p>
        </div>

        {/* Copy link fallback */}
        <button
          onClick={copyLink}
          className="w-full py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition-colors flex items-center justify-center gap-2"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-600">Link copied!</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy invite link instead
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function LinkBadge({ status }) {
  const styles = {
    active:  'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    revoked: 'bg-slate-200 text-slate-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || styles.revoked}`}>
      {status === 'active' ? '✓ Linked' : status === 'pending' ? 'Pending' : 'Revoked'}
    </span>
  )
}

function ConnectedView({ coParent, onRevoke }) {
  const [confirming, setConfirming] = useState(false)
  const navigate = useNavigate()

  return (
    <div className="space-y-4">
      {/* Partner card */}
      <div className="card p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {(coParent.partner_name || coParent.partner_email || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-800 text-base">{coParent.partner_name || 'Co-Parent'}</p>
            <p className="text-sm text-slate-500 truncate">{coParent.partner_email || ''}</p>
            <LinkBadge status="active" />
          </div>
        </div>
      </div>

      {/* What's shared */}
      <div className="card p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700">Shared Between You</h3>
        {[
          { icon: '👶', label: 'Child profiles', desc: 'Both see and update children' },
          { icon: '📅', label: 'Shared calendar events', desc: 'Events marked "shared" sync both ways' },
          { icon: '📋', label: 'Court orders', desc: 'Both see all orders in the system' },
        ].map(item => (
          <div key={item.label} className="flex items-start gap-3">
            <span className="text-xl">{item.icon}</span>
            <div>
              <p className="text-sm font-medium text-slate-700">{item.label}</p>
              <p className="text-sm text-slate-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Message Co-Parent */}
      <button
        onClick={() => navigate('/direct-messages')}
        className="w-full card p-4 flex items-center gap-4 active:scale-[0.98] transition-transform text-left"
      >
        <div className="w-11 h-11 rounded-2xl brand-gradient flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">Message Co-Parent</p>
          <p className="text-sm text-slate-500 mt-0.5">Send a secure, court-friendly message</p>
        </div>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-slate-400 flex-shrink-0">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Disconnect */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-1">Disconnect Co-Parent</h3>
        <p className="text-sm text-slate-500 mb-3">
          Disconnecting stops future sharing. Existing data is not deleted.
        </p>
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="w-full py-3 rounded-xl text-sm font-medium text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-colors"
          >
            Disconnect Co-Parent
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-red-700 font-medium text-center">Are you sure?</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirming(false)}>Cancel</Button>
              <button
                onClick={() => onRevoke(coParent.id)}
                className="flex-1 py-3 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Yes, Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function InviteView({ user }) {
  const [email, setEmail]         = useState('')
  const [sending, setSending]     = useState(false)
  const [resending, setResending] = useState(false)
  const [sent, setSent]           = useState(false)
  const [sentToken, setSentToken] = useState(null)
  const [error, setError]         = useState('')
  const [canResend, setCanResend] = useState(false)  // true when server says invite already pending
  const [qrToken, setQrToken]     = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const { sendCoParentInvite }    = useApp()

  const handleSend = async () => {
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }
    if (email.toLowerCase() === user?.email?.toLowerCase()) {
      setError("You can't invite yourself")
      return
    }
    setSending(true)
    setError('')
    setCanResend(false)
    try {
      const invite = await sendCoParentInvite(email.trim())
      setSentToken(invite?.token || null)
      setSent(true)
    } catch (e) {
      const msg = e.message || 'Could not send invite. Please try again.'
      setError(msg)
      // Surface resend option when a pending invite already exists for this address
      if (msg.toLowerCase().includes('already pending')) setCanResend(true)
    } finally {
      setSending(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    setCanResend(false)
    try {
      const invite = await api.data.resendInvite(email.trim())
      setSentToken(invite?.invite?.token || null)
      setSent(true)
    } catch (e) {
      setError(e.message || 'Could not resend invite. Please try again.')
    } finally {
      setResending(false)
    }
  }

  const handleGenerateQR = async () => {
    setQrLoading(true)
    setError('')
    try {
      const data = await api.data.generateQRInvite()
      setQrToken(data.token)
    } catch (e) {
      setError(e.message || 'Could not generate QR code. Please try again.')
    } finally {
      setQrLoading(false)
    }
  }

  if (sent) {
    return (
      <>
        {qrToken && <QRModal token={qrToken} onClose={() => setQrToken(null)} />}
        <div className="card p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="font-bold text-slate-800">Invite Sent!</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            An invitation was sent to <strong>{email}</strong>. They'll see a prompt to accept when they sign up or log in.
          </p>
          {sentToken && (
            <button
              onClick={() => setQrToken(sentToken)}
              className="w-full py-3 rounded-xl bg-brand-700 text-white text-sm font-semibold flex items-center justify-center gap-2 mb-3 active:scale-[0.98] transition-transform"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
              Show QR Code
            </button>
          )}
          <button
            onClick={() => { setSent(false); setEmail(''); setSentToken(null) }}
            className="text-sm text-blue-600 font-medium"
          >
            Send another invite
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      {qrToken && <QRModal token={qrToken} onClose={() => setQrToken(null)} />}
      <div className="card p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-slate-800">Invite Your Co-Parent</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Enter their email — or generate a QR code to share in person.
          </p>
        </div>

        <Input
          label="Co-parent's email"
          type="email"
          placeholder="coparent@example.com"
          value={email}
          onChange={e => { setEmail(e.target.value); setError('') }}
        />
        {error && (
          <div className="space-y-2">
            <p className="text-sm text-red-600">{error}</p>
            {canResend && (
              <button
                onClick={handleResend}
                disabled={resending}
                className="w-full py-2.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {resending ? (
                  <span className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
                {resending ? 'Resending…' : 'Resend Invite'}
              </button>
            )}
          </div>
        )}

        <Button onClick={handleSend} disabled={sending || !email.trim()} className="w-full">
          {sending ? 'Sending…' : 'Send Invite by Email'}
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-500 font-medium">or</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* QR Code button */}
        <button
          onClick={handleGenerateQR}
          disabled={qrLoading}
          className="w-full py-3.5 rounded-2xl border-2 border-brand-700 text-brand-700 text-sm font-semibold flex items-center justify-center gap-2.5 active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {qrLoading ? (
            <span className="w-4 h-4 border-2 border-brand-700 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          )}
          {qrLoading ? 'Generating…' : 'Generate QR Code'}
        </button>
        <p className="text-sm text-center text-slate-500 -mt-2">
          Show your screen — co-parent scans to connect instantly
        </p>
      </div>
    </>
  )
}

function AcceptView({ scannedToken = '' }) {
  const [token, setToken]        = useState(scannedToken)
  const [loading, setLoading]    = useState(false)
  const [success, setSuccess]    = useState(false)
  const [error, setError]        = useState('')
  const fromQR                   = !!scannedToken
  const { acceptCoParentInvite } = useApp()

  // If a scanned token arrives after mount (e.g. tab switch), sync it in
  useEffect(() => {
    if (scannedToken) setToken(scannedToken)
  }, [scannedToken])

  const handleAccept = async () => {
    if (!token.trim()) return
    setLoading(true)
    setError('')
    try {
      await acceptCoParentInvite(token.trim())
      setSuccess(true)
    } catch (e) {
      setError(e.message || 'Invalid or expired token. Please check and try again.')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="card p-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-3xl">🔗</span>
        </div>
        <p className="font-bold text-slate-800">Accounts Linked!</p>
        <p className="text-sm text-slate-500 mt-1">
          You and your co-parent are now connected. Shared calendar events, children, and court orders will sync automatically.
        </p>
      </div>
    )
  }

  return (
    <div className="card p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-slate-800">Accept an Invite</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          {fromQR
            ? 'QR code detected — your invite token is ready. Tap Accept to connect.'
            : 'Paste the invite token from the email you received, or the link shared by your co-parent.'}
        </p>
      </div>

      {/* QR-detected banner */}
      {fromQR && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          <p className="text-sm font-medium text-green-800">Invite token scanned from QR code ✓</p>
        </div>
      )}

      <Input
        label="Invite Token"
        placeholder="Paste token here…"
        value={token}
        onChange={e => { setToken(e.target.value); setError('') }}
      />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button onClick={handleAccept} disabled={loading || !token.trim()} className="w-full">
        {loading ? 'Connecting…' : 'Accept Invite'}
      </Button>
    </div>
  )
}

function WhyCoParentBanner() {
  return (
    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 space-y-2">
      <p className="text-sm font-semibold text-blue-800">Why connect accounts?</p>
      <ul className="space-y-1.5">
        {[
          'See each other\'s shared custody calendar events',
          'Both parents can view and track court orders',
          'Child profiles and school info stay in sync',
          'Court-ready documentation that shows cooperation',
        ].map(item => (
          <li key={item} className="flex items-start gap-2 text-sm text-blue-700">
            <span className="text-blue-400 mt-0.5 flex-shrink-0">✓</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Pending invites list ──────────────────────────────────────────────────────
function PendingInvitesList() {
  const [invites,     setInvites]     = useState([])
  const [loading,     setLoading]     = useState(true)
  const [resendingId, setResendingId] = useState(null)
  const [deletingId,  setDeletingId]  = useState(null)
  const [toastMsg,    setToastMsg]    = useState('')
  const [toastType,   setToastType]   = useState('success') // 'success' | 'error'

  const showToast = (msg, type = 'success') => {
    setToastMsg(msg)
    setToastType(type)
    setTimeout(() => setToastMsg(''), 3000)
  }

  const load = useCallback(async () => {
    try {
      const data = await api.data.getInvites()
      setInvites((data.sent || []).filter(i => i.status === 'pending'))
    } catch (_) {
      // silently ignore — list just won't show
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleResend = async (invite) => {
    setResendingId(invite.id)
    try {
      await api.data.resendInvite(invite.invite_email)
      showToast(`Invite resent to ${invite.invite_email}`)
      await load()
    } catch (e) {
      showToast(e.message || 'Could not resend', 'error')
    } finally {
      setResendingId(null)
    }
  }

  const handleDelete = async (invite) => {
    setDeletingId(invite.id)
    try {
      await api.data.revokeInvite(invite.id)
      showToast(`Invitation to ${invite.invite_email} cancelled`)
      await load()
    } catch (e) {
      showToast(e.message || 'Could not cancel invitation', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading || invites.length === 0) return null

  const busy = (id) => resendingId === id || deletingId === id

  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm font-semibold text-slate-700">Pending Invitations</p>

      {toastMsg && (
        <div className={`flex items-center gap-2 rounded-xl px-3 py-2 border ${
          toastType === 'success'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          {toastType === 'success' ? (
            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <p className={`text-sm ${toastType === 'success' ? 'text-green-800' : 'text-red-700'}`}>{toastMsg}</p>
        </div>
      )}

      {invites.map(invite => (
        <div key={invite.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
          {/* Avatar */}
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 text-sm font-bold">
            {invite.invite_email[0].toUpperCase()}
          </div>

          {/* Email + status */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{invite.invite_email}</p>
            <p className="text-xs text-amber-600 font-medium">Awaiting acceptance</p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Resend */}
            <button
              onClick={() => handleResend(invite)}
              disabled={busy(invite.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 text-xs font-semibold transition-colors disabled:opacity-50"
            >
              {resendingId === invite.id ? (
                <span className="w-3.5 h-3.5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              {resendingId === invite.id ? 'Sending…' : 'Resend'}
            </button>

            {/* Delete / Cancel */}
            <button
              onClick={() => handleDelete(invite)}
              disabled={busy(invite.id)}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-500 transition-colors disabled:opacity-50"
              aria-label="Cancel invitation"
            >
              {deletingId === invite.id ? (
                <span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Invite/Accept tab panel ───────────────────────────────────────────────────
// Defined OUTSIDE CoParent so React never treats it as a new component type on
// re-render — if defined inside, every state change would remount the panel and
// destroy the focused <input>, causing the iOS keyboard to immediately dismiss.
function InvitePanel({ tab, setTab, urlToken, user }) {
  return (
    <div className="space-y-4">
      <div className="flex bg-slate-100 rounded-xl p-1" role="tablist" aria-label="Co-parent actions">
        <button
          onClick={() => setTab('invite')}
          role="tab"
          aria-selected={tab === 'invite'}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'invite' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-700'
          }`}
        >
          Send Invite
        </button>
        <button
          onClick={() => setTab('accept')}
          role="tab"
          aria-selected={tab === 'accept'}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'accept' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-700'
          }`}
        >
          Accept Invite
        </button>
      </div>
      {tab === 'invite' ? (
        <InviteView user={user} />
      ) : (
        <AcceptView scannedToken={urlToken} />
      )}
    </div>
  )
}

export default function CoParent() {
  const { user, coParents = [], revokeCoParentLink } = useApp()
  const location   = useLocation()
  const urlToken   = new URLSearchParams(location.search).get('token') || ''
  const [tab, setTab]           = useState(urlToken ? 'accept' : 'invite')
  // Start expanded if url has ?invite=1 (from Dashboard shortcut)
  const wantsInvite = new URLSearchParams(location.search).get('invite') === '1'
  const [showAddAnother, setShowAddAnother] = useState(wantsInvite)
  const inviteRef = useRef(null)

  const hasLinked = coParents.length > 0

  // If a token arrives via URL (QR scan), switch to Accept tab automatically
  useEffect(() => {
    if (urlToken) setTab('accept')
  }, [urlToken])

  // Scroll invite section into view when opened via ?invite=1
  useEffect(() => {
    if (wantsInvite && inviteRef.current) {
      setTimeout(() => inviteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300)
    }
  }, [wantsInvite])

  const handleRevoke = async (id) => {
    await revokeCoParentLink(id)
  }

  return (
    <div className="flex flex-col h-full bg-white lg:bg-slate-50">
      <AppHeader title="Co-Parent" />
      <div className="screen no-scrollbar px-4 sm:px-6 lg:px-8 py-5">
        <div className="mx-auto w-full max-w-6xl space-y-4">

          {hasLinked ? (
            <>
              {/* Status banner */}
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-2xl px-3 py-2.5">
                <span className="text-green-500 text-lg">🔗</span>
                <p className="text-sm font-semibold text-green-800">
                  {coParents.length === 1
                    ? 'Co-parent account linked'
                    : `${coParents.length} co-parent accounts linked`}
                </p>
              </div>

              {/* One card per linked co-parent */}
              {coParents.map(cp => (
                <ConnectedView key={cp.id} coParent={cp} onRevoke={handleRevoke} />
              ))}

              {/* Add Another toggle */}
              <div ref={inviteRef} className="card p-4">
                <button
                  onClick={() => setShowAddAnother(v => !v)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-brand-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Add Another Co-Parent</p>
                      <p className="text-xs text-slate-500">Blended families, guardians, or step-parents</p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-slate-400 transition-transform ${showAddAnother ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showAddAnother && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <InvitePanel tab={tab} setTab={setTab} urlToken={urlToken} user={user} />
                  </div>
                )}
              </div>

              {/* Pending invites list — always visible when invites are outstanding */}
              <PendingInvitesList />
            </>
          ) : (
            <>
              {/* Not linked — original empty state */}
              <div className="text-center py-2 lg:pt-4">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">👨‍👩‍👧</span>
                </div>
                <h2 className="text-lg font-bold text-slate-800">Connect With Your Co-Parent</h2>
                <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                  Link your accounts to share a custody calendar, court orders, and child profiles.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-5 space-y-4">
                  <WhyCoParentBanner />
                  <div className="flex items-start gap-2 text-sm text-slate-500">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p>Linking is mutual and requires acceptance from both parties. You can disconnect at any time. Private calendar events are never shared.</p>
                  </div>
                </div>
                <div className="lg:col-span-7 space-y-4">
                  <InvitePanel tab={tab} setTab={setTab} urlToken={urlToken} user={user} />
                  <PendingInvitesList />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
